import { usePackage, useProjectId, useServiceName } from '@consigli/hooks';
import { ServiceCategory, ServiceName } from '@consigli/types';
import clsx from 'clsx';
import { FC, ReactNode, useMemo, useCallback } from 'react';
import { useParams } from 'react-router';

import { Breadcrumbs } from '@/features/breadcrumb/breadcrumbs';
import { useViewerContext, ViewerMode } from '@/features/document-viewer/use-viewer-context';
import {
  BidReviewService,
  getSelectedBidReviewServices,
} from '@/features/package-creation/bid-review/types';
import { useBidReviewContractors } from '@/features/package-creation/bid-review/use-bid-review-contractors';
import { BuildingPackageList } from '@/features/package-list/building-package-list';
import { DocumentPackageList } from '@/features/package-list/document-package-list';
import { LayoutPackageList } from '@/features/package-list/layout-package-list';
import { StructuralPackageList } from '@/features/package-list/structural-package-list';
import { useWorkspaceContext } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceTabs } from '@/layouts/workspace-layout/workspace-tabs';
import { Route } from '@/routes';
import { TabConfig } from '@/util/types';

const INSIGHT_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.DASHBOARD}`,
    name: 'servicetabs.overview',
  },
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.FINDINGS}`,
    name: 'servicetabs.findings',
  },
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.DOCUMENTS}`,
    name: 'servicetabs.documents',
  },
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.CATEGORIZATION}`,
    name: 'servicetabs.folders',
  },
];

const LAYOUT_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, layoutId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${layoutId}/${Route.INPUT}`,
    name: 'optimization-tabs.input',
  },
  {
    href: (serviceName, projectId, layoutId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${layoutId}/${Route.RESULTS}`,
    name: 'optimization-tabs.results',
  },
];

const STRUCTURAL_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.INPUT}`,
    name: 'structural-tabs.input',
  },
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.RESULTS}`,
    name: 'structural-tabs.results',
  },
];

const MEETING_SECRETARY_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, _unused) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.DASHBOARD}`,
    name: 'servicetabs.dashboard',
  },
  {
    href: (serviceName, projectId, _unused) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.MEETINGS}`,
    name: 'servicetabs.meetings',
  },
  {
    href: (serviceName, projectId, _unused) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.CALENDAR}`,
    name: 'servicetabs.calendar',
  },
  {
    href: (serviceName, projectId, _unused) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.TEMPLATES}`,
    name: 'servicetabs.templates',
  },
  {
    href: (serviceName, projectId, _unused) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.SETTINGS}`,
    name: 'servicetabs.settings',
  },
];

const BUILDING_PACKAGE_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, buildingPackageId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${buildingPackageId}/${Route.INPUT}`,
    name: 'optimization-tabs.input',
  },
  {
    href: (serviceName, projectId, buildingPackageId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${buildingPackageId}/${Route.RESULTS}`,
    name: 'optimization-tabs.results',
  },
];

const SCHEDULE_CHECK_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, scheduleCheckId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${scheduleCheckId}/${Route.COMPARISON}`,
    name: 'schedule-check-tabs.comparison',
  },
  {
    href: (serviceName, projectId, scheduleCheckId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${scheduleCheckId}/${Route.DOCUMENTS}`,
    name: 'schedule-check-tabs.documents',
  },
];

const LUMINAIRE_SCHEDULE_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, packageId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.RESULTS}`,
    name: 'luminaire-schedule-tabs.results',
  },
  {
    href: (serviceName, projectId, packageId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.DOCUMENTS}`,
    name: 'luminaire-schedule-tabs.documents',
  },
];

const REPORT_GENERATION_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, packageId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.DOCUMENTS}`,
    name: 'report-generation-tabs.documents',
  },
  {
    href: (serviceName, projectId, packageId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.RESULTS}`,
    name: 'report-generation-tabs.results',
  },
];

const SITE_DUE_DILIGENCE_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, packageId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.DOCUMENTS}`,
    name: 'servicetabs.documents',
  },
  {
    href: (serviceName, projectId, packageId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.REPORTS}`,
    name: 'servicetabs.reports',
  },
  {
    href: (serviceName, projectId, packageId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.LOCATION}`,
    name: 'servicetabs.location',
  },
];

const TENDER_DOCUMENTS_TABS: TabConfig[] = [
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.SUMMARY}`,
    name: 'servicetabs.summary',
  },
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.DASHBOARD}`,
    name: 'servicetabs.overview',
  },
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.FINDINGS}`,
    name: 'servicetabs.findings',
  },
  {
    href: (serviceName, projectId, serviceId) =>
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.DOCUMENTS}`,
    name: 'servicetabs.documents',
  },
];

// dashboard tab and drops the folders tab. Which tabs show depends on the
// bid-review services the user picked in the creation wizard (persisted on the
// package): Risk/Insight/Gaps drives the dashboard + findings tabs, Bid vs. Bid
// drives the comparison tab. Documents is always available.
const BID_REVIEW_DASHBOARD_TAB: TabConfig = {
  href: (serviceName, projectId, serviceId) =>
    `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.DASHBOARD}`,
  name: 'servicetabs.risks-insights-gaps',
};

const BID_REVIEW_COMPARISON_TAB: TabConfig = {
  href: (serviceName, projectId, serviceId) =>
    `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.BID_COMPARISON}`,
  name: 'servicetabs.bid-comparison',
};

const BID_REVIEW_FINDINGS_TAB: TabConfig = {
  href: (serviceName, projectId, serviceId) =>
    `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.FINDINGS}`,
  name: 'servicetabs.findings',
};

const BID_REVIEW_DOCUMENTS_TAB: TabConfig = {
  href: (serviceName, projectId, serviceId) =>
    `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${serviceId}/${Route.DOCUMENTS}`,
  name: 'servicetabs.documents',
};

// Packages created before the services attribute existed have no stored
// services, so fall back to the full tab set — but only include the comparison
// tab once the package is known to have contractor-tagged bids. Legacy OFFER
// packages resolve no contractors and have no comparison data, so they never get
// the comparison tab (and, while contractors are still loading, we withhold it
// rather than showing it and snapping it away).
const buildBidReviewTabs = (services: BidReviewService[], hasContractors: boolean): TabConfig[] => {
  if (services.length === 0) {
    return hasContractors
      ? [
          BID_REVIEW_DASHBOARD_TAB,
          BID_REVIEW_COMPARISON_TAB,
          BID_REVIEW_FINDINGS_TAB,
          BID_REVIEW_DOCUMENTS_TAB,
        ]
      : [BID_REVIEW_DASHBOARD_TAB, BID_REVIEW_FINDINGS_TAB, BID_REVIEW_DOCUMENTS_TAB];
  }
  const tabs: TabConfig[] = [];
  if (services.includes(BidReviewService.RISK_INSIGHT_GAPS)) {
    tabs.push(BID_REVIEW_DASHBOARD_TAB);
  }
  if (services.includes(BidReviewService.BID_VS_BID)) {
    tabs.push(BID_REVIEW_COMPARISON_TAB);
  }
  if (services.includes(BidReviewService.RISK_INSIGHT_GAPS)) {
    tabs.push(BID_REVIEW_FINDINGS_TAB);
  }
  tabs.push(BID_REVIEW_DOCUMENTS_TAB);
  return tabs;
};

type WorkspaceLayoutProps = {
  children: ReactNode;
  hideTabs?: boolean;
  // Optional props for building packages to override default tab calculation
  customTabs?: TabConfig[];
};

export const WorkspaceLayout: FC<WorkspaceLayoutProps> = ({ children, hideTabs, customTabs }) => {
  const { mode } = useViewerContext();
  const { isOverlayVisible } = useWorkspaceContext();
  const serviceName = useServiceName();
  const params = useParams<Record<string, string>>();
  const projectId = useProjectId();
  const packageId = params['packageId'] ?? '';
  const { pkg: documentPackage } = usePackage(projectId, packageId);

  const isBidReview = serviceName === ServiceName.BID_REVIEW;
  const { contractors } = useBidReviewContractors({
    skip: !isBidReview || !packageId,
  });
  const hasContractors = contractors.length > 0;

  const serviceCategory = useMemo(() => {
    if (serviceName === ServiceName.MEETING_SECRETARY) {
      return ServiceCategory.MEETING_SECRETARY;
    }
    if (serviceName === ServiceName.SCHEDULE_CHECK) {
      return ServiceCategory.SCHEDULE_CHECK;
    }
    if (serviceName === ServiceName.LUMINAIRE_SCHEDULE) {
      return ServiceCategory.LUMINAIRE_SCHEDULE;
    }
    if (serviceName === ServiceName.REPORT_GENERATION) {
      return ServiceCategory.REPORT_GENERATION;
    }
    if (params['packageId']) {
      return ServiceCategory.INSIGHT;
    } else if (params['layoutId']) {
      return ServiceCategory.LAYOUT_OPTIMIZATION;
    } else if (params['structuralId']) {
      return ServiceCategory.STRUCTURAL_ENGINEERING;
    } else if (params['buildingPackageId']) {
      return ServiceCategory.BUILDING_PACKAGE;
    }
    return ServiceCategory.MEETING_SECRETARY; // fallback
  }, [serviceName, params]);

  const serviceId = useMemo(() => {
    if (serviceCategory === ServiceCategory.MEETING_SECRETARY) {
      return '';
    }
    const possibleIdNames = ['packageId', 'structuralId', 'layoutId', 'buildingPackageId'] as const;
    for (const idName of possibleIdNames) {
      const id = params[idName];
      if (id) {
        return id;
      }
    }
    return '';
  }, [serviceCategory, params]);

  const filterTabs = useCallback(
    (serviceName: string, tabs: TabConfig[]) => {
      const showFoldersTab =
        serviceName === ServiceName.MANAGEMENT_OPERATIONS_MAINTENANCE &&
        documentPackage?.needClassification;
      return showFoldersTab ? tabs : tabs.filter((obj) => obj.name !== 'servicetabs.folders');
    },
    [documentPackage?.needClassification],
  );

  const bidReviewTabs = useMemo(
    () =>
      buildBidReviewTabs(
        documentPackage ? getSelectedBidReviewServices(documentPackage) : [],
        hasContractors,
      ),
    [documentPackage, hasContractors],
  );

  const tabs = useMemo(() => {
    if (customTabs) {
      return customTabs;
    }
    if (serviceName === ServiceName.REPORT_GENERATION) {
      return REPORT_GENERATION_TABS;
    }
    if (serviceName === ServiceName.SITE_DUE_DILIGENCE) {
      return SITE_DUE_DILIGENCE_TABS;
    }
    if (serviceName === ServiceName.TENDER_DOCUMENTS) {
      return TENDER_DOCUMENTS_TABS;
    }
    if (serviceName === ServiceName.BID_REVIEW) {
      return bidReviewTabs;
    }
    switch (serviceCategory) {
      case ServiceCategory.INSIGHT:
        return filterTabs(serviceName, INSIGHT_TABS);
      case ServiceCategory.LAYOUT_OPTIMIZATION:
        return LAYOUT_TABS;
      case ServiceCategory.STRUCTURAL_ENGINEERING:
        return STRUCTURAL_TABS;
      case ServiceCategory.MEETING_SECRETARY:
        return MEETING_SECRETARY_TABS;
      case ServiceCategory.BUILDING_PACKAGE:
        return BUILDING_PACKAGE_TABS;
      case ServiceCategory.SCHEDULE_CHECK:
        return SCHEDULE_CHECK_TABS;
      case ServiceCategory.LUMINAIRE_SCHEDULE:
        return LUMINAIRE_SCHEDULE_TABS;
      default:
        return [];
    }
    // TODO:Remove user.isSuperuser from the code after testing
  }, [serviceCategory, serviceName, customTabs, filterTabs, bidReviewTabs]);

  const scrollListComponent = useMemo(() => {
    switch (serviceCategory) {
      case ServiceCategory.INSIGHT:
      case ServiceCategory.SCHEDULE_CHECK:
      case ServiceCategory.LUMINAIRE_SCHEDULE:
      case ServiceCategory.REPORT_GENERATION:
        return <DocumentPackageList />;
      case ServiceCategory.LAYOUT_OPTIMIZATION:
        return <LayoutPackageList />;
      case ServiceCategory.STRUCTURAL_ENGINEERING:
        return <StructuralPackageList />;
      case ServiceCategory.BUILDING_PACKAGE:
        return <BuildingPackageList />;
      default:
        return null;
    }
  }, [serviceCategory]);

  return (
    <main className="flex flex-row w-full h-full bg-day-light-4">
      <div
        className={clsx(
          'flex flex-col w-full',
          mode !== ViewerMode.DocumentFullscreen && 'mx-auto',
        )}
      >
        <div className="flex flex-col">
          <div
            className={clsx(
              'flex items-center justify-center 2xl:py-8 py-4 transition-colors duration-300',
              isOverlayVisible && 'bg-white',
            )}
          >
            <div className="flex flex-row grow max-w-[2000px] px-8 mx-auto">
              <Breadcrumbs />
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-center items-center w-full">
            <WorkspaceTabs
              hideTabs={hideTabs}
              tabs={customTabs || tabs}
              serviceCategory={serviceCategory}
              serviceId={serviceId}
              scrollListComponent={scrollListComponent}
            />
          </div>
        </div>
        {/* Deliberately no overflow rule, for the same reason as RootViewport: the
            document is the scroll container here, so an `overflow` would make this a
            scrollport that never scrolls and leave anything a page pins (such as the
            version-compare table headers) resolving against it and never sticking. */}
        <div className="flex-1">
          <div className="max-w-[2000px] mx-auto h-full min-h-[85vh]">{children}</div>
        </div>
      </div>
    </main>
  );
};
